import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, MessageCircle } from "lucide-react";
import { fmtPrice, waLink, displayName } from "@/data/site";
import { ProductImage } from "./ProductImage";

const QuickViewContext = createContext(() => {});
export const useQuickView = () => useContext(QuickViewContext);

export const QuickViewProvider = ({ children }) => {
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const open = useCallback((p) => {
    setActiveImg(0);
    setProduct(p);
  }, []);
  const close = useCallback(() => setProduct(null), []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  useEffect(() => {
    document.body.style.overflow = product ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [product]);

  const images = product ? [product.image, product.imageAlt].filter(Boolean) : [];
  const discount =
    product?.oldPrice && product.oldPrice > product.price
      ? Math.round((1 - product.price / product.oldPrice) * 100)
      : 0;

  return (
    <QuickViewContext.Provider value={open}>
      {children}
      <AnimatePresence>
        {product && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-6"
            data-testid="quickview-modal"
          >
            <div
              className="absolute inset-0 bg-emerald-deep/70 backdrop-blur-md"
              onClick={close}
              data-testid="quickview-backdrop"
            />
            <motion.div
              initial={{ y: 60, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 40, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full sm:max-w-3xl bg-ivory max-h-[92svh] overflow-y-auto scrollbar-none grid grid-cols-1 sm:grid-cols-2"
            >
              <button
                onClick={close}
                aria-label="Kapat"
                data-testid="quickview-close"
                className="absolute top-3 right-3 z-10 h-11 w-11 rounded-full bg-white/90 backdrop-blur border border-gold-muted inline-flex items-center justify-center text-ink transition-colors hover:text-gold"
              >
                <X size={18} />
              </button>

              {/* Gallery */}
              <div className="bg-white p-6 sm:p-8 flex flex-col">
                <div className="relative aspect-square">
                  <ProductImage
                    key={images[activeImg]}
                    src={images[activeImg]}
                    alt={product.name}
                    className="absolute inset-0 h-full w-full object-contain"
                    testId="quickview-image"
                  />
                  {discount > 0 && (
                    <span className="absolute top-0 left-0 bg-emerald text-ivory font-mono text-[11px] font-medium tracking-wider px-2.5 py-1">
                      -%{discount}
                    </span>
                  )}
                </div>
                {images.length > 1 && (
                  <div className="mt-4 flex gap-2 justify-center">
                    {images.map((src, i) => (
                      <button
                        key={src}
                        onClick={() => setActiveImg(i)}
                        data-testid={`quickview-thumb-${i + 1}`}
                        aria-label={`Fotoğraf ${i + 1}`}
                        className={`h-14 w-14 bg-white border p-1 transition-colors ${
                          activeImg === i ? "border-gold" : "border-gold-muted/50 hover:border-gold/60"
                        }`}
                      >
                        <ProductImage src={src} alt={product.name} className="h-full w-full object-contain" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-6 sm:p-8 flex flex-col">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">{product.brand}</p>
                <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-semibold text-ink leading-tight" data-testid="quickview-title">
                  {displayName(product.name)}
                </h3>

                <div className="mt-4 flex items-baseline gap-3" data-testid="quickview-price">
                  <span className="text-2xl sm:text-3xl font-semibold tracking-tight text-emerald whitespace-nowrap">
                    {fmtPrice(product.price)}
                  </span>
                  {product.oldPrice && (
                    <span className="text-base text-sage line-through whitespace-nowrap">
                      {fmtPrice(product.oldPrice)}
                    </span>
                  )}
                </div>

                <p className="mt-2 flex items-center gap-2 text-[13px]" data-testid="quickview-stock">
                  <span className={`h-1.5 w-1.5 rounded-full ${product.available ? "bg-emerald" : "bg-red-800"}`} />
                  <span className={product.available ? "text-sage" : "text-red-800 font-medium"}>
                    {product.available ? "Stokta mevcut" : "Tükendi"}
                  </span>
                </p>

                <div className="mt-6 flex flex-col gap-3">
                  {product.available ? (
                    <a
                      href={product.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="quickview-buy"
                      className="flex h-[52px] items-center justify-center gap-2 rounded-full bg-emerald text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-ink"
                    >
                      Satın Al — Mağazaya Git
                      <ArrowUpRight size={15} />
                    </a>
                  ) : (
                    <span className="flex h-[52px] items-center justify-center rounded-full bg-sand text-sm font-semibold text-sage cursor-not-allowed">
                      Stokta Yok
                    </span>
                  )}
                  <a
                    href={waLink(
                      `Merhaba, Atılmaz Saat üzerinden ${displayName(product.name)} (Fiyat: ${fmtPrice(product.price)}) ürünü hakkında bilgi almak ve sipariş vermek istiyorum.`
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="quickview-whatsapp"
                    className="flex h-[52px] items-center justify-center gap-2 rounded-full border border-emerald/20 text-sm font-semibold text-emerald transition-colors duration-300 hover:border-emerald"
                  >
                    <MessageCircle size={16} />
                    WhatsApp'tan Sipariş Ver
                  </a>
                </div>

                <p className="mt-auto pt-6 text-xs leading-relaxed text-sage">
                  Orijinallik garantisi ve özel kutusuyla gönderilir. Satış ve güvenli ödeme
                  atilmazsaat.com üzerinden tamamlanır.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </QuickViewContext.Provider>
  );
};
